require('font-awesome/css/font-awesome.css');
require('./Root.sass');

import packageJson from '../package.json';

import 'material-icons/iconfont/material-icons.css';
import 'material-symbols';

import React, { useEffect } from 'react';
import { BrowserRouter as Router, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router';

import { Provider } from 'react-redux';
import { store } from './redux/store';
import { I18nextProvider } from 'react-i18next';
import { EntitySelectorWrapperContext } from '@ts/storybook';

import * as Sentry from '@sentry/react';
import envJson from '../.env.json';

// The order of these imports matters.
// First, we import default storybook colors and styles.
import '@ts/storybook/dist/storybook.css';
// Then, we override them with tracksphere colors.
import './index.css';

import App from './App';

import Config from '@gral/config.json';

// Configure JSS RTL
const { create } = require('jss');
const rtl = require('jss-rtl').default;
const { StylesProvider, jssPreset } = require('@mui/styles');
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { IAppProps } from '@interfaces/App.interface';
import { CustomTheme } from './public/customization/CustomTheme';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { RouterProvider } from '@utils/context/RouterContext';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import i18nInstance from '@utils/i18n';

interface RootProps extends IAppProps {}

const cache = createCache({
  key: 'css',
  prepend: true,
});

const TracksphereApp = (props: RootProps) => {
  const lng = i18nInstance.language;
  const useEffectInitSentry = () => {
    useEffect(() => {
      document.title = Config.customer.applicationName;
      if (envJson.NODE_ENV !== 'development') {
        Sentry.init({
          dsn: envJson.SENTRY_DSN,
          environment: envJson.ENVIRONMENT,
          integrations: [
            Sentry.replayIntegration(),
            Sentry.reactRouterV6BrowserTracingIntegration({
              useEffect: React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
          ],
          release: `${packageJson.name}@${packageJson.version}`,

          // Set tracesSampleRate to 1.0 to capture 100%
          // of transactions for performance monitoring.
          tracesSampleRate: 1.0,

          // Capture Replay for 10% of all sessions,
          // plus for 100% of sessions with an error
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
      }
      return () => {};
    }, []);
  };

  useEffectInitSentry();

  const theme = CustomTheme(store, lng);

  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18nInstance}>
        <StyledEngineProvider injectFirst>
          <CacheProvider value={cache}>
            <StylesProvider jss={jss}>
              <ThemeProvider theme={theme}>
                <EntitySelectorWrapperContext.Provider value={WrapperEntitySelector as any}>
                  <Router>
                    <RouterProvider>
                      <App {...props} />
                    </RouterProvider>
                  </Router>
                </EntitySelectorWrapperContext.Provider>
              </ThemeProvider>
            </StylesProvider>
          </CacheProvider>
        </StyledEngineProvider>
      </I18nextProvider>
    </Provider>
  );
};

export default TracksphereApp;
